import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as motion from 'motion/react-client';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import ProgressCircular from '../general/ProgressCircular';
import XLight from '@/assets/xLight.svg';
import NovaAttachMenu from './NovaAttachMenu';
import { NovaAttachIcon, NovaSendIcon, NovaStopIcon } from './novaIcons';
import { novaThemes, novaDisclaimer, NovaThemeName } from './novaTheme';

// Tailwind needs statically-discoverable class names, so the transform-origin can't be
// built with a template literal — look it up instead.
const ORIGIN_CLASS = {
  top: { left: 'origin-bottom-left', right: 'origin-bottom-right' },
  bottom: { left: 'origin-top-left', right: 'origin-top-right' },
} as const;

// Closes the "+" popover on any click outside its own subtree (button + menu card).
const useClickOutside = (ref: React.RefObject<HTMLElement | null>, open: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [ref, open, onClose]);
};

interface NovaComposerProps {
  onSend: (query: string) => void;
  // Opens NovaUploadDialog, optionally pre-filled with files already chosen via the
  // "+" menu's own file picker (see NovaAttachMenu) — mirrors NovaDocuments page's
  // openUploadDialog(files) pattern rather than requiring a second browse step.
  onAttach?: (files?: File[]) => void;
  // A document already indexed from a past chat (see NovaAttachMenu) — appended
  // straight to pendingFilePaths, same as a freshly completed upload.
  onAttachExistingDocument?: (relativeName: string) => void;
  userProfileId?: string;
  waiting?: boolean;
  streaming?: boolean;
  onStop?: () => void;
  hasMessages?: boolean;
  // Fires on every keystroke (and on send/clear, with '') — lets a caller drive a
  // "lean in while typing" indicator without lifting the whole draft into its own state.
  onDraftChange?: (value: string) => void;
  variant: 'master' | 'compact';
  theme: NovaThemeName;
  published12mFilter: boolean;
  onTogglePublished12mFilter: (value: boolean) => void;
  // Uploaded-but-not-yet-sent documents (NovaUploadDialog), shown as chips below
  // the input so the user can see what's about to go out with their next message.
  pendingFilePaths?: string[];
  onRemovePendingFilePath?: (filePath: string) => void;
}

// Pending file paths are `${uuid}_${originalFilename}` (see NovaUploadDialog) —
// strip the uuid so the chip shows the name the user actually recognizes.
const UUID_PREFIX_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;
const formatAttachmentName = (filePath: string) => filePath.replace(UUID_PREFIX_RE, '');

const TEXTAREA_MAX_HEIGHT = 120;

const NovaComposer: React.FC<NovaComposerProps> = ({
  onSend,
  onAttach,
  onAttachExistingDocument,
  userProfileId,
  waiting,
  streaming,
  onStop,
  hasMessages,
  onDraftChange,
  variant,
  theme,
  published12mFilter,
  onTogglePublished12mFilter,
  pendingFilePaths,
  onRemovePendingFilePath,
}) => {
  const [query, setQuery] = useState('');
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [attachMenuPlacement, setAttachMenuPlacement] = useState<{ vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' }>({
    vertical: 'bottom',
    horizontal: 'left',
  });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const attachMenuRootRef = useRef<HTMLDivElement>(null);
  const attachMenuRef = useRef<HTMLDivElement>(null);
  const surfaces = novaThemes[theme];
  const prefersReducedMotion = useReducedMotion();
  useClickOutside(attachMenuRootRef, attachMenuOpen, () => setAttachMenuOpen(false));

  // The popover defaults to opening below-left of the "+" button, but that overflows
  // the viewport when the composer sits near the bottom or edge of the screen (e.g. the
  // follow-up composer). Flip to whichever side actually has room, and recheck whenever
  // the menu's own content resizes (root view vs. the taller "Nova Documents" list).
  useLayoutEffect(() => {
    if (!attachMenuOpen) {
      setAttachMenuPlacement({ vertical: 'bottom', horizontal: 'left' });
      return;
    }
    const recalcPlacement = () => {
      const buttonEl = attachMenuRootRef.current;
      const menuEl = attachMenuRef.current;
      if (!buttonEl || !menuEl) return;
      const buttonRect = buttonEl.getBoundingClientRect();
      const menuRect = menuEl.getBoundingClientRect();
      const margin = 8;

      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const vertical = spaceBelow < menuRect.height + margin && spaceAbove > spaceBelow ? 'top' : 'bottom';

      const spaceRight = window.innerWidth - buttonRect.left;
      const horizontal = spaceRight < menuRect.width + margin ? 'right' : 'left';

      setAttachMenuPlacement({ vertical, horizontal });
    };

    recalcPlacement();
    const menuEl = attachMenuRef.current;
    const resizeObserver = new ResizeObserver(recalcPlacement);
    if (menuEl) resizeObserver.observe(menuEl);
    window.addEventListener('resize', recalcPlacement);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', recalcPlacement);
    };
  }, [attachMenuOpen]);

  const onDraftChangeRef = useRef(onDraftChange);
  useEffect(() => {
    onDraftChangeRef.current = onDraftChange;
  });
  useEffect(() => () => onDraftChangeRef.current?.(''), []);

  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
      ta.style.overflowY = ta.scrollHeight > TEXTAREA_MAX_HEIGHT ? 'auto' : 'hidden';
    }
  }, [query]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (waiting || !query.trim()) return;
    onSend(query);
    setQuery('');
    onDraftChange?.('');
  };

  const toggle = (className: string) => (
    <label className={`inline-flex shrink-0 cursor-pointer items-center gap-2 text-[13px] whitespace-nowrap ${className}`}>
      <span
        className="relative inline-flex shrink-0 rounded-full transition-colors"
        style={{ width: 32, height: 18, background: published12mFilter ? surfaces.actionAccent : surfaces.surfaces.toggleTrackOff }}
      >
        <input
          type="checkbox"
          checked={published12mFilter}
          onChange={(e) => onTogglePublished12mFilter(e.target.checked)}
          className="sr-only"
        />
        <span
          className="absolute rounded-full transition-transform"
          style={{
            top: 2,
            width: 14,
            height: 14,
            transform: `translateX(${published12mFilter ? 16 : 2}px)`,
            background: published12mFilter ? surfaces.ink.onGold : '#FFFFFF',
          }}
        />
      </span>
      <span style={{ color: surfaces.ink.secondary }}>Last 12 months</span>
    </label>
  );

  return (
    <div className="flex flex-col gap-2">
      <form
        onSubmit={submit}
        className="flex w-full items-center gap-2"
        style={{
          minHeight: variant === 'master' ? 60 : 48,
          borderRadius: variant === 'master' ? 30 : 24,
          background: variant === 'master' ? (theme === 'dark' ? surfaces.surfaces.field : surfaces.surfaces.card) : surfaces.surfaces.field,
          border: `1px solid ${variant === 'master' && theme === 'dark' ? surfaces.surfaces.hairline : surfaces.surfaces.composerBorder}`,
          boxShadow: variant === 'master' && theme === 'light' ? '0 10px 32px -18px rgba(30, 28, 22, 0.3)' : 'none',
          padding: variant === 'master' ? '8px 7px 8px 12px' : '6px 5px 6px 10px',
        }}
      >
        {onAttach && (
          <div className="relative shrink-0" ref={attachMenuRootRef}>
            <button
              type="button"
              onClick={() => setAttachMenuOpen((v) => !v)}
              disabled={waiting}
              className={`flex shrink-0 cursor-pointer items-center justify-center rounded-full ${variant === 'master' ? 'h-[34px] w-[34px]' : 'h-[28px] w-[28px]'}`}
              style={{ color: surfaces.ink.placeholder }}
              aria-label="Add to this question"
              aria-expanded={attachMenuOpen}
            >
              <NovaAttachIcon className="h-4 w-4" />
            </button>
            <AnimatePresence>
              {attachMenuOpen && (
                <motion.div
                  ref={attachMenuRef}
                  className={[
                    'absolute z-20',
                    attachMenuPlacement.vertical === 'top' ? 'bottom-full mb-2' : 'top-full mt-2',
                    attachMenuPlacement.horizontal === 'right' ? 'right-0' : 'left-0',
                    ORIGIN_CLASS[attachMenuPlacement.vertical][attachMenuPlacement.horizontal],
                  ].join(' ')}
                  initial={
                    prefersReducedMotion
                      ? { opacity: 1 }
                      : { opacity: 0, scale: 0.95, y: attachMenuPlacement.vertical === 'top' ? 6 : -6 }
                  }
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={
                    prefersReducedMotion
                      ? { opacity: 1 }
                      : { opacity: 0, scale: 0.95, y: attachMenuPlacement.vertical === 'top' ? 6 : -6 }
                  }
                  transition={{ duration: 0.16, ease: 'easeOut' }}
                >
                  <NovaAttachMenu
                    theme={theme}
                    onClose={() => setAttachMenuOpen(false)}
                    onAttachFiles={(files) => onAttach(files)}
                    userProfileId={userProfileId}
                    onDocumentAttached={(relativeName) => onAttachExistingDocument?.(relativeName)}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
        <textarea
          ref={textareaRef}
          className="min-h-6 w-full resize-none bg-transparent text-base font-medium transition-colors outline-none placeholder:text-[color:var(--nova-placeholder)]"
          style={{ color: surfaces.ink.heading, '--nova-placeholder': surfaces.ink.placeholder } as React.CSSProperties}
          onChange={(e) => {
            setQuery(e.target.value);
            onDraftChange?.(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit(e);
            }
          }}
          rows={1}
          placeholder={hasMessages ? 'Ask a follow-up…' : 'Ask Nova.ai anything…'}
          disabled={waiting}
          value={query}
        />
        {variant === 'master' && toggle('mr-3.5 max-sm:hidden')}
        {/* Same footprint as the send button, with the ring centred in it — right-aligned, it sat against the field's edge. */}
        <div className={`flex shrink-0 items-center justify-center ${variant === 'master' ? 'w-[46px]' : 'w-[38px]'}`}>
          {streaming && onStop ? (
            <button
              type="button"
              onClick={onStop}
              className={`flex shrink-0 cursor-pointer items-center justify-center transition-opacity hover:opacity-85 ${variant === 'master' ? 'h-[46px] w-[46px]' : 'h-[38px] w-[38px]'}`}
              style={{ background: surfaces.actionAccent, color: surfaces.ink.onGold, borderRadius: '50%' }}
              aria-label="Stop generating"
              title="Stop generating"
            >
              <NovaStopIcon className={variant === 'master' ? 'h-[19px] w-[19px]' : 'h-4 w-4'} />
            </button>
          ) : waiting ? (
            <ProgressCircular
              indeterminate
              size={variant === 'master' ? 22 : 18}
              width={3}
              colorClassName={theme === 'dark' ? 'stroke-[#F3FE88]' : 'stroke-[#FDC212]'}
              bgClassName={theme === 'dark' ? 'stroke-white/15' : 'stroke-black/10'}
            />
          ) : (
            <button
              type="submit"
              className={`flex shrink-0 cursor-pointer items-center justify-center transition-opacity disabled:opacity-50 ${variant === 'master' ? 'h-[46px] w-[46px]' : 'h-[38px] w-[38px]'}`}
              style={{ background: surfaces.actionAccent, color: surfaces.ink.onGold, borderRadius: '50%' }}
              disabled={waiting || !query.trim()}
              aria-label="Send"
            >
              <NovaSendIcon className={variant === 'master' ? 'h-[19px] w-[19px]' : 'h-4 w-4'} />
            </button>
          )}
        </div>
      </form>

      {variant === 'master' && <div className="px-3 sm:hidden">{toggle('')}</div>}

      {pendingFilePaths && pendingFilePaths.length > 0 && (
        <div className="flex flex-wrap gap-2 px-1">
          {pendingFilePaths.map((filePath) => (
            <div
              key={filePath}
              className="flex items-center gap-1.5 rounded-full py-1 pr-2 pl-3 text-xs"
              style={{
                background: surfaces.surfaces.field,
                border: `1px solid ${surfaces.surfaces.composerBorder}`,
                color: surfaces.ink.body,
              }}
            >
              <span className="max-w-[180px] truncate">{formatAttachmentName(filePath)}</span>
              {onRemovePendingFilePath && (
                <XLight
                  className="h-3 w-3 shrink-0 cursor-pointer opacity-60 transition-opacity hover:opacity-100"
                  onClick={() => onRemovePendingFilePath(filePath)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {variant === 'compact' && (
        <div
          className="flex items-center justify-between text-[10px]"
          style={{ padding: '6px 16px 14px', color: surfaces.ink.placeholder }}
        >
          <label className="flex shrink-0 cursor-pointer items-center gap-[8px]">
            <span
              className="relative inline-flex shrink-0 rounded-full transition-colors"
              style={{
                width: 34,
                height: 19,
                background: published12mFilter ? surfaces.actionAccent : surfaces.surfaces.toggleTrackOff,
              }}
            >
              <input
                type="checkbox"
                checked={published12mFilter}
                onChange={(e) => onTogglePublished12mFilter(e.target.checked)}
                className="sr-only"
              />
              <span
                className="absolute rounded-full transition-transform"
                style={{
                  top: 2,
                  width: 15,
                  height: 15,
                  transform: `translateX(${published12mFilter ? 17 : 2}px)`,
                  background: published12mFilter ? surfaces.ink.onGold : '#FFFFFF',
                }}
              />
            </span>
            <span className="whitespace-nowrap" style={{ color: surfaces.ink.secondary }}>
              Last 12 months
            </span>
          </label>
          <span className="whitespace-nowrap">{novaDisclaimer.short}</span>
        </div>
      )}
    </div>
  );
};

export default NovaComposer;
